import { ref, computed, type Ref } from 'vue';
import dayjs from 'dayjs';

import { searchesLib, pricesLib } from '../lib';
import type { MappedRoundTripNearestSearch } from '../types';

export const useRoundTripNearestSearches = (
  nearestDepartureSearches: Ref<MappedRoundTripNearestSearch[]>,
  nearestArrivalSearches: Ref<MappedRoundTripNearestSearch[]>,
) => {
  const departureSearches = ref<MappedRoundTripNearestSearch[]>([]);
  const rawArrivalSearches = ref<MappedRoundTripNearestSearch[]>([]);

  const activeDeparture = computed(() =>
    searchesLib.findSelectedDate(departureSearches.value),
  );
  const filteredArrivalSearches = computed(() =>
    searchesLib.filterValidArrivalSearches(
      rawArrivalSearches.value,
      activeDeparture.value?.departureDate,
    ),
  );
  const activeArrival = computed(() =>
    searchesLib.findSelectedDate(filteredArrivalSearches.value),
  );

  const isDepartureAfterArrival = computed(() => {
    return dayjs(activeDeparture.value.departureDate).isSameOrAfter(
      activeArrival.value.departureDate,
    );
  });

  const init = (departureDates: string[]) => {
    departureSearches.value = searchesLib.changeSelectedDate(
      nearestDepartureSearches.value,
      'departureDate',
      departureDates[0],
    );

    rawArrivalSearches.value = searchesLib.changeSelectedDate(
      nearestArrivalSearches.value,
      'departureDate',
      departureDates[1],
    );
  };

  const handleDepartureSearchesUpdate = () => {
    if (isDepartureAfterArrival.value) {
      /** If departure date is after arrival date, the arrival searches should be cut
       *  and first available element in arrival searches is automatically set active,
       *  so user can't choose arrival date earlier than departure date
       * */
      const newActiveColumnInd =
        nearestArrivalSearches.value.length -
        filteredArrivalSearches.value.length;
      rawArrivalSearches.value = searchesLib.changeSelectedDate(
        rawArrivalSearches.value,
        'id',
        rawArrivalSearches.value[newActiveColumnInd].id,
      );
    }
  };

  const updateDepartureSearches = (id: number) => {
    if (id !== activeDeparture.value?.id) {
      departureSearches.value = searchesLib.changeSelectedDate(
        nearestDepartureSearches.value,
        'id',
        id,
      );
      handleDepartureSearchesUpdate();
    }
  };

  const updateArrivalSearches = (id: number) => {
    if (id !== activeArrival.value?.id) {
      rawArrivalSearches.value = searchesLib.changeSelectedDate(
        nearestArrivalSearches.value,
        'id',
        id,
      );
    }
  };

  const totalPrice = computed(() => {
    return pricesLib.getTotalPrice(activeDeparture.value, activeArrival.value);
  });

  const daysDifference = computed(() => {
    return dayjs(activeArrival.value.departureDate).diff(
      activeDeparture.value.departureDate,
      'd',
    );
  });

  return {
    init,
    departureSearches,
    filteredArrivalSearches,
    totalPrice,
    updateArrivalSearches,
    updateDepartureSearches,
    activeDeparture,
    activeArrival,
    daysDifference,
  };
};
